<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, watch, computed } from 'vue'
import { api, type SignalData, type EnrichedSynthNavData, type VixIntraday, type EtfTrickKtData, type AlphaVantageHealth, type DataCsvFreshness, type DatabentoEquityHealth, type KtIntradayLiveHealth, type HoldingsStatusData, type ImrWarmupHealth, type BgEffectiveDateHealth, type BgaTradeCoherence, type NavCrossChecks, type GoldSignalResponse, type GgeSignalResponse, type GoldKtLatestResponse, type GgeKtLatestResponse, type KtAttributionResponse, type BgaJhSynthNavLive } from '../api/client'
import { useAuth } from '../composables/useAuth'
import { useViewAs } from '../composables/useViewAs'
import { useIntegrityChecks, signalPillUp, signalPillFund, type Category } from '../composables/useIntegrityChecks'
import { useFundEnablement } from '../composables/useFundEnablement'
import { usePortfolio, usePage } from '../composables/usePortfolio'
import { useVisibleFunds } from '../composables/useVisibleFunds'
import { useAgentFindings, type AgentPillTone } from '../composables/useAgentFindings'
import AccountPanel from '../components/AccountPanel.vue'
import IdentityPill from '../components/IdentityPill.vue'
import PnlPanel from '../components/PnlPanel.vue'
import NavPanel from '../components/nav/NavPanel.vue'
import RiskPanel from '../components/RiskPanel.vue'
import ExecPanel from '../components/ExecPanel.vue'
import AdminPanel from '../components/AdminPanel.vue'
import StrategyPanel from '../components/StrategyPanel.vue'
import BlueBoxPill from '../components/BlueBoxPill.vue'
import PhonePill from '../components/PhonePill.vue'
import OtpWindowPanel from '../components/OtpWindowPanel.vue'
import OtpBacktestPanel from '../components/OtpBacktestPanel.vue'
import ManualSellAlertBanner from '../components/ManualSellAlertBanner.vue'
import PricingSourceAlertBanner from '../components/PricingSourceAlertBanner.vue'
import SignalHealthAlertBanner from '../components/SignalHealthAlertBanner.vue'
import StrikeAnchorHealthTile from '../components/StrikeAnchorHealthTile.vue'
import KtTrackingPanel from '../components/KtTrackingPanel.vue'
import { fundForPortfolio } from '../components/nav/fundConfig'
import { readCookies, readLastFund, rememberLastFund } from '../components/nav/lastFund'
import { rowHref } from '../components/fof/fofFormat'
import { FUND_PAGES, FOF_PAGE, tabsFor, feed, type SignalTabCtx, type TabState, type FundTabId, type FundPage, type HomePage } from '../components/nav/fundPages'
import FofHome from '../components/fof/FofHome.vue'
import FofBacktestPanel from '../components/fof/FofBacktestPanel.vue'
import DisabledTabBody from '../components/fundtabs/DisabledTabBody.vue'
import BgaJhSignalTab from '../components/fundtabs/BgaJhSignalTab.vue'
import DevOpsPanel from '../components/devops/DevOpsPanel.vue'

const { portfolio } = usePortfolio()
// FOF spec 2026-10-12: the PAGE (navigation). 'fof' = the FOF home page; otherwise the same
// slug as `portfolio` (the fund context, which stays 'bga' on FOF so fund panels never see 'fof').
const { page: pageSlug } = usePage()
// Superuser view-only access (2026-09-28): a gated fund page (Japan) the
// caller can view but is not entitled to shows an amber Preview badge on
// every tab — dealing (plan legs, the tick, liquidate) still needs a grant.
const { isPreview, visiblePortfolios } = useVisibleFunds()
const showPreviewBadge = computed(() => isPreview(portfolio.value))
// Plan jh-bga-ui-consolidation Task 5: derive fund discriminator for fund-aware API calls.
// Gold routes to BGA (Gold has its own dedicated paths and never reaches JH-branched code).
const fund = computed<'BGA' | 'JH'>(() => fundForPortfolio(portfolio.value) === 'JH' ? 'JH' : 'BGA')
// F2 (review fix, 2026-08-16): separate fund selector for the holdings-
// status integrity poll ONLY. `fund` above deliberately routes gold->BGA
// for intSignal/intEnriched/etc — those have no GOLD equivalent wired
// through this ref (gold uses its own intGoldSignal/intGoldEnriched computeds
// over the FUND_PAGES.gold.feeds data instead — see mountFeedsOnce/feedData
// above). But GOLD's OWN rebalance/
// status marker (ninety_one_gold_last_rebalance.json, decoupled-file read
// via HoldingsUpdateService.StatusForFund) needs its own fund value, not
// BGA's, or /holdings/status?fund=GOLD is never reached from here.
//
// Japan fund page UI Task 6: derived from the fund registry. Any fund the
// holdings-status endpoint doesn't model (OTP, JAPAN) keeps the historical
// BGA fallback, so behaviour is identical for every live portfolio.
const holdingsStatusFund = computed<'BGA' | 'JH' | 'GOLD' | 'GGE'>(() => {
  const f = fundForPortfolio(portfolio.value)
  return f === 'JH' || f === 'GOLD' || f === 'GGE' ? f : 'BGA'
})
type TabId = 'home' | 'signal' | 'pnl' | 'nav' | 'risk' | 'kt' | 'account' | 'exec' | 'devops' | 'otp' | 'backtest' | 'admin' | 'strategy'
const validTabs: TabId[] = ['home', 'signal', 'pnl', 'nav', 'risk', 'kt', 'account', 'exec', 'devops', 'otp', 'backtest', 'admin', 'strategy']

const { loading, isAuthenticated, isSuperUser, init, setGoToLogin } = useAuth()

// DevOps agent pills (paolo 2026-10-03): the DevOps tab's Platform health (P)
// and Strategy analysis (S) verdicts in the status bar, detail rows in the
// integrity dropdown. One shared poll (useAgentFindings), signed-in only —
// /api/devops/findings needs auth, like the DevOps tab itself.
const agentFindings = useAgentFindings()
const agentPlatform = agentFindings.platform
const agentStrategy = agentFindings.strategy
const agentRows = computed(() => [agentPlatform.value, agentStrategy.value])
watch(isAuthenticated, (authed) => {
  if (authed) agentFindings.start()
  else agentFindings.stop()
}, { immediate: true })
onUnmounted(() => { if (isAuthenticated.value) agentFindings.stop() })
function toneIcon(tone: AgentPillTone) {
  return tone === 'err' ? '\u2717' : tone === 'warn' ? '\u26A0' : tone === 'off' ? '\u2013' : '\u2713'
}
// Linked users Task 9 (spec 2026-09-28): the Admin tab is hidden while a
// superuser is viewing as another user — Paolo keeps full superuser powers
// while viewing as (the amber identity pill + stripe stay up so he can
// never write unknowingly, see IdentityPill.vue), but Admin's
// user-management surface has no place inside someone else's view.
const { target: viewAsTarget } = useViewAs()

// otp-standalone Plan C Task 3 (spec §3.4): the otp portfolio gets a
// reduced, otp-only tab set — it has no signal/pnl/nav/risk/account/exec/
// strategy screens (those are fund-dashboard concepts; OTP is a residual-
// cash overlay, not a fund share class). Every OTHER portfolio loses the
// 'otp' tab entirely — it now lives ONLY on the otp portfolio, replacing
// the old per-fund OTP tab (OtpPanel/GoldOtpPanel) which is no longer
// mounted from here (see the tab-content template below).
//
// Declared BEFORE getTabFromHash/activeTab (task-3-review.md finding 1
// fix) — hash resolution below reads availableTabs.value, so it (and its
// isSuperUser/portfolio deps) must already be live before the first
// getTabFromHash() call in activeTab's initializer just below.
//
// Japan fund page UI Task 6: fund portfolios take their tab list from the
// FUND_PAGES registry (tabsFor = FUND_TAB_ORDER minus superuser tabs for
// standard users); the otp overlay page keeps its own list below.
const page = computed<FundPage | HomePage>(() => pageSlug.value === 'fof' ? FOF_PAGE : FUND_PAGES[portfolio.value])

// Paolo 2026-11-03: Home shows the same tab bar as a fund page. These tabs open on
// Home; the rest link to the last fund visited (cookie, BGA by default).
const HOME_LOCAL_TABS: ReadonlySet<TabId> = new Set<TabId>(['home', 'backtest', 'account', 'admin'])
const lastFundSlug = computed(() => readLastFund(readCookies(), visiblePortfolios.value))
const lastFundPage = computed(() => FUND_PAGES[lastFundSlug.value] as FundPage & { kind: 'fund' })
function homeTabHref(tab: TabId): string | null {
  if (page.value.kind !== 'home' || HOME_LOCAL_TABS.has(tab)) return null
  return `${rowHref(lastFundSlug.value, window.location.hostname)}#${tab}`
}
function selectTab(tab: TabId) {
  const href = homeTabHref(tab)
  if (href) window.location.assign(href)
  else activeTab.value = tab
}
watch(page, (p) => {
  if (p.kind === 'fund') rememberLastFund(portfolio.value, window.location.hostname)
}, { immediate: true })

// Plan 2026-10-04 Task 4a: registry-mounted integrity feeds. One shared
// bag, keyed by FundFeedSpec.id; thin computed()s expose each entry under
// its historical ref name so useIntegrityChecks's positional signature is
// preserved. The visible-pages watcher re-mounts feeds whenever /me (and
// hence entitlement) resolves, so a late JAPAN grant does start the Japan
// feed without a page reload (Review Focus #1).
const feedData = reactive<Record<string, unknown>>({})
const feedTimers: ReturnType<typeof setInterval>[] = []
const mountedFeedIds = new Set<string>()
// Declared here (not with pillFund below) because the first feed tick runs
// synchronously inside the immediate visible-pages watcher just under
// mountFeedsOnce — a later `const` would still be in its TDZ there.
const { hasAccess, whenLoaded } = useFundEnablement()

function mountFeedsOnce(pages: ReadonlyArray<FundPage>) {
  for (const p of pages) {
    if (p.kind !== 'fund') continue           // overlay (otp) has no feeds — Review Focus #5
    for (const f of p.feeds) {
      if (mountedFeedIds.has(f.id)) continue
      mountedFeedIds.add(f.id)
      const tick = async () => {
        // 2026-11-02: a per-user fund route (FundFeedSpec.fund) is skipped,
        // not requested, while the viewer lacks that fund — the server would
        // only 403 it (same rule as the Signal pill, #1119). hasAccess fails
        // OPEN until /me resolves, so at most the first tick can go out.
        if (f.fund) {
          // Wait for /me so the first tick is decided, never a fail-open 403.
          await whenLoaded()
          if (!hasAccess(f.fund)) { feedData[f.id] = null; return }
        }
        try {
          feedData[f.id] = await f.fetch()
        } catch {
          // Final-review fix wave (2026-10-04): keepPreviousOnFail feeds
          // (ktJh/ktGge/japanSignal) keep whatever feedData already holds —
          // see the 2026-08-30 rationale on FundFeedSpec.
          if (!f.keepPreviousOnFail) feedData[f.id] = null
        }
      }
      // `tick` is async, so a synchronous throw inside it (e.g. an undefined
      // api method called before `await`) becomes a rejected promise that
      // the INNER try/catch above already handles — there is no outer
      // synchronous throw left to catch here.
      void tick()
      feedTimers.push(setInterval(tick, f.intervalMs))
    }
  }
}

const vf = useVisibleFunds()
watch(() => vf.visiblePortfolios.value, (slugs) => {
  mountFeedsOnce(slugs.map(s => FUND_PAGES[s]))
}, { immediate: true })

onUnmounted(() => { feedTimers.forEach(clearInterval); feedTimers.length = 0 })

const availableTabs = computed<TabId[]>(() => {
  const p = page.value
  const viewingAs = viewAsTarget.value !== null
  // Paolo 2026-11-03 (replaces FOF spec 2026-10-12 §3.4's Home/Backtest/Account/Admin):
  // Home first, then the last fund's own tab bar. Home/Backtest/Account/Admin open
  // on Home; every other tab links to that tab on the last fund (homeTabHref).
  if (p.kind === 'home') {
    const tabs: TabId[] = ['home', ...tabsFor(lastFundPage.value, isSuperUser.value)]
    return viewingAs ? tabs.filter(t => t !== 'admin') : tabs
  }
  if (p.kind === 'overlay') {
    const tabs: TabId[] = ['otp', 'backtest', 'devops']
    if (isSuperUser.value && !viewingAs) tabs.push('admin')
    return tabs
  }
  const tabs = tabsFor(p, isSuperUser.value)
  return viewingAs ? tabs.filter(t => t !== 'admin') : tabs
})

// Japan fund page UI Task 7: per-tab enabled/disabled state, driven by the
// FUND_PAGES registry. Overlay pages (otp) have no `tabs` map — every tab
// there is implicitly enabled. `t in page.value.tabs` guards a fund page
// whose tabs map doesn't (yet) declare every FundTabId.
const tabState = (t: TabId): TabState =>
  page.value.kind === 'home' && homeTabHref(t)
    ? lastFundPage.value.tabs[t as FundTabId]
  : page.value.kind === 'fund' && t in page.value.tabs
    ? page.value.tabs[t as FundTabId]
    : { kind: 'enabled' }

// task-3-review.md finding 1 (Critical): hash resolution must validate
// against the CURRENT portfolio's availableTabs, not the flat validTabs
// superset — otherwise a stale/typed `#otp` hash on a fund portfolio still
// mounts OtpWindowPanel (reopening the spec §3.4 leak), and a deep link
// like `#pnl`/`#exec` on the otp portfolio renders a hidden fund tab
// instead of falling back to 'otp'. 'account' is exempted from the
// availableTabs check: it is the universal login/session gate forced by
// setGoToLogin/the isAuthenticated watch below on EVERY portfolio
// (including otp, whose availableTabs deliberately excludes an 'account'
// tab button) — without this exemption, the watch(activeTab) hash-sync
// below would round-trip `#account` back through here, fail the
// availableTabs check on the otp portfolio, and immediately undo the
// forced login redirect.
function getTabFromHash(): TabId {
  const raw = window.location.hash.replace('#', '')
  const tab = raw.split('/')[0] as TabId
  const fallback: TabId = page.value.kind === 'home' ? 'home' : portfolio.value === 'otp' ? 'otp' : 'signal'
  if (!validTabs.includes(tab)) return fallback
  if (tab === 'account') return tab
  // A fund tab on FOF is a link to the last fund, never a Home body (#pnl on FOF → home).
  if (page.value.kind === 'home' && !HOME_LOCAL_TABS.has(tab)) return fallback
  return availableTabs.value.includes(tab) ? tab : fallback
}

const activeTab = ref<TabId>(getTabFromHash())
const apiHealthy = ref(false)
const hlConnected = ref(false)

setGoToLogin(() => { activeTab.value = 'account' })

function tabLabel(tab: TabId): string {
  if (tab === 'home') return 'Home'
  if (tab === 'pnl') return 'P&L'
  if (tab === 'nav') return 'NAV'
  if (tab === 'risk') return 'Risk'
  if (tab === 'kt') return 'K_t'
  if (tab === 'otp') return 'OTP'
  if (tab === 'admin') return 'Admin'
  if (tab === 'strategy') return 'Strategy'
  if (tab === 'devops') return 'DevOps'
  return tab.charAt(0).toUpperCase() + tab.slice(1)
}

// When not authenticated, force to account tab
watch(isAuthenticated, (authed) => {
  if (!authed && !loading.value) {
    activeTab.value = 'account'
  }
})

// otp-standalone Plan C Task 5: cold-start unauthenticated visits to the otp
// portfolio must land on the account/login tab, not the otp tab. The
// watch(isAuthenticated) above only catches a SESSION EXPIRING mid-visit
// (authenticated -> unauthenticated transition) — it never fires for a
// browser that was never authenticated, because isAuthenticated's initial
// value is already `false` and Vue's watch only reacts to changes. Fund
// dashboards deliberately allow unauthenticated browsing of the public
// signal tab (SignalPanel's "Log in to view" link), but the otp portfolio
// has no public content — every OtpWindowPanel data source is superuser-
// gated — so it gets its own cold-start guard here, scoped to
// portfolio === 'otp' so fund dashboards keep their existing behaviour.
watch(loading, (isLoading) => {
  if (!isLoading && !isAuthenticated.value && portfolio.value === 'otp') {
    activeTab.value = 'account'
  }
})

// Sync URL hash with active tab (preserve sub-hash for pnl / nav)
watch(activeTab, (tab) => {
  if ((tab === 'pnl' || tab === 'nav' || tab === 'devops') && !isAuthenticated.value) {
    activeTab.value = 'account'
    return
  }
  // Don't clobber sub-hash if the panel manages its own suffix
  const current = window.location.hash.replace('#', '')
  if (tab === 'pnl' && current.startsWith('pnl/')) return
  if (tab === 'nav' && current.startsWith('nav/')) return
  if (tab === 'admin' && current.startsWith('admin/')) return
  if (tab === 'strategy' && current.startsWith('strategy/')) return
  window.location.hash = tab
})

// Listen for browser back/forward navigation
function onHashChange() {
  const tab = getTabFromHash()
  if (tab !== activeTab.value) activeTab.value = tab
}
window.addEventListener('hashchange', onHashChange)
onUnmounted(() => {
  window.removeEventListener('hashchange', onHashChange)
  if (healthRetryTimer) { clearInterval(healthRetryTimer); healthRetryTimer = null }
})

// Bug fix 2 (2026-09-29): initialise() used to `return` early when
// api.health() failed, so init() (auth) never ran — a superuser who
// happened to load the page while the API was briefly unhealthy could
// never sign in or see the identity pill/tabs resolve, even after the API
// recovered, because nothing ever re-checked health. init() now always
// runs; health is polled independently every 5s until it reports healthy,
// so `apiHealthy` (driving the API status pill) recovers on its own.
let healthRetryTimer: ReturnType<typeof setInterval> | null = null

async function checkHealth() {
  apiHealthy.value = await api.health().catch(() => false)
}

async function initialise() {
  await checkHealth()
  if (!apiHealthy.value) {
    healthRetryTimer = setInterval(async () => {
      await checkHealth()
      if (apiHealthy.value && healthRetryTimer) {
        clearInterval(healthRetryTimer)
        healthRetryTimer = null
      }
    }, 5000)
  }

  await init()

  // Fix-wave review (F3, final-review.md): getTabFromHash() validates the
  // hash against availableTabs, which is computed off isSuperUser —
  // `false` at setup time, before init() resolves auth. activeTab's
  // initializer (line ~111) therefore ran the FIRST getTabFromHash() call
  // against a non-superuser availableTabs set, and nothing re-resolves it
  // afterwards: the hashchange listener only fires on a hash CHANGE, and
  // the activeTab->hash sync watch below never fires because activeTab
  // itself never changed. Net effect: a superuser pressing F5 on
  // #backtest/#admin/#strategy (fund portfolios) or #admin (otp portfolio)
  // always landed on signal/otp instead. Re-resolve now that isSuperUser
  // is live — the location hash is still intact at this point, so this is
  // exactly the deferred first resolution, not a navigation.
  const resolvedTab = getTabFromHash()
  if (resolvedTab !== activeTab.value) activeTab.value = resolvedTab

  try {
    const status = await api.status()
    hlConnected.value = status.loggedIn
  } catch {
    hlConnected.value = false
  }
}

onMounted(initialise)

// ── Integrity checks (polled alongside status bar) ──
const intSignal = ref<SignalData | null>(null)
// The Signal pill's own payload: intSignal when the viewer has the page fund, else
// the other BGA/JH signal (signalPillFund); null fund = no pill.
const pillFund = computed(() => signalPillFund(fund.value, hasAccess))
const pillSignal = ref<SignalData | null>(null)
const intEnriched = ref<EnrichedSynthNavData | null>(null)
// 2026-11-04: BGA's/JH's one live value, the shadow K_t (synth_nav:{bga|jh}:live) — the hero and sparkline read only
// it (the engine's synth − FVA and enriched-payload returns are no longer shown).
const intShadowLive = ref<BgaJhSynthNavLive | null>(null)
const intVixIntraday = ref<VixIntraday | null>(null)
// K_t series feeds the `fresh-kt-recency` / coverage / 7d-residual checks
// in useIntegrityChecks. Polled every 5 min — the daily K_t writer changes
// at most once per day, so 5s polling alongside signal/enriched is wasteful.
// Without this ref the kt-recency check sees `undefined` and fires fail
// — the bug surfaced 2026-05-08 ("No K_t series data" at top of screen).
const intKt = ref<EtfTrickKtData[] | null>(null)
// 2026-08-07: JH-scoped K_t series — fetched from /api/synthetic/jh/kt.
// Drives fresh-jh-kt-recency / cohere-jh-kt-residual / complete-jh-kt-coverage
// in useIntegrityChecks. Same 5-min cadence as BGA's intKt — daily writer,
// so higher-frequency polling is wasteful. Without this ref the 3 new JH
// K_t checks silently pass with 'No JH K_t series data' every tick, defeating
// the purpose of adding them — see the 2026-07-23→2026-08-07 stall for the
// class of bug this catches.
// Plan 2026-10-04 Task 4b: moved into FUND_PAGES.jh.feeds (ktJh) — read
// through feedData like the cluster-A computeds above, preserving
// useIntegrityChecks's positional signature.
const intKtJh = computed<EtfTrickKtData[] | null>(() => feed<EtfTrickKtData[]>('ktJh', feedData))
// GGE-scoped daily K_t series (2026-08-23 Phase-1 K_t enhancement, P2 of
// [[project_backlog_kt_observability_2026-08-23]]). GGE has 3,661 rows in
// `synthetic_bga_kt` (deepest history of any fund) so we can back a
// series-based Postgres recency + residual check for it, complementary
// to the `:kt_latest` Redis-scalar checks shipped in PR #193. Catches
// the "Redis publisher fresh / Postgres writer stalled" class (JH hit
// this 2026-07-23→08-07, 15-day silent stall with zero panel signal).
// Gold does NOT get this — 0 rows in synthetic_bga_kt (distinct writer
// path); its series check would always return empty. Same 5-min cadence
// as intKt/intKtJh — daily writer.
// Plan 2026-10-04 Task 4b: moved into FUND_PAGES.gge.feeds (ktGge).
const intKtGge = computed<EtfTrickKtData[] | null>(() => feed<EtfTrickKtData[]>('ktGge', feedData))
// K_t ticker-attribution refs (2026-08-23 Phase-1 enhancement #3).
// Per-fund top-N residual-contribution breakdown from
// /api/synthetic/{fund}/kt-attribution. Feeds the
// cohere-{fund}-kt-top-contributor integrity rows on the BGA + JH K_t
// sections. 5-min cadence — the underlying inputs (CSV closes + holdings
// snapshots) only advance once daily.
// Plan 2026-10-04 Task 4b: moved into FUND_PAGES.bga/jh.feeds
// (ktAttributionBga/ktAttributionJh).
const intKtAttributionBga = computed<KtAttributionResponse | null>(() => feed<KtAttributionResponse>('ktAttributionBga', feedData))
const intKtAttributionJh  = computed<KtAttributionResponse | null>(() => feed<KtAttributionResponse>('ktAttributionJh', feedData))
// AV health snapshot feeds the `fresh-alphavantage-daily` check. Polled
// every 5 min — the AV probe itself fires every 6h so faster polling is
// wasteful (and the cached payload stays stable between probes).
const intAvHealth = ref<AlphaVantageHealth | null>(null)
// Local-CSV freshness feeds the `fresh-vix-ohlc-csv` check (and future
// per-file extensions). Polled every 5 min — CSVs roll forward at most
// once per day, so the cached payload is stable between cycles.
const intCsvFreshness = ref<DataCsvFreshness | null>(null)
// Databento equity-feed health feeds `fresh-databento-equity`. Polled
// every 60s during the US RTH window — the keys roll forward on every
// 1m bar, so we want tighter cadence than AV/CSV (5min) but slower than
// the signal/enriched stream (5s).
const intDatabentoEquity = ref<DatabentoEquityHealth | null>(null)
// K_t intraday live-adapter health (2026-05-17). Probes latest:kt_intraday
// + morning anchor + override consistency. Polled every 60s alongside the
// Databento check — same cadence-of-change reasoning.
// Plan 2026-10-04 Task 4b: moved into FUND_PAGES.bga.feeds (ktIntradayLive).
const intKtIntradayLive = computed<KtIntradayLiveHealth | null>(() => feed<KtIntradayLiveHealth>('ktIntradayLive', feedData))
// JH-scoped counterpart (Task 11, JH K_t live-parity plan). Fetched from
// kt-intraday-live?fund=jh so the jh-kt-* integrity checks read JH's OWN
// pipeline health rather than silently reusing BGA's intKtIntradayLive
// object — same 60s cadence as the BGA probe above. Moved into
// FUND_PAGES.jh.feeds (ktIntradayLiveJh) in Task 4b.
const intKtIntradayLiveJh = computed<KtIntradayLiveHealth | null>(() => feed<KtIntradayLiveHealth>('ktIntradayLiveJh', feedData))
// Holdings status feeds the BGA holdings cross-check (BG vs HL weight
// mismatches, missing names). 30s cadence matches HoldingsPanel.
const intHoldingsStatus = ref<HoldingsStatusData | null>(null)
// IMR warm-up status (2026-05-18). Only changes on signal_service
// restart; 60s poll matches the other Redis-key probes.
const intImrWarmup = ref<ImrWarmupHealth | null>(null)
// BG effective-date probe (2026-05-24). Written by the weekly full BG
// fetch loop, so the cached payload is effectively day-stable — a 10-min
// poll picks up post-fetch advance within the same hour.
const intBgEffective = ref<BgEffectiveDateHealth | null>(null)
// BGA trade FIFO-coherence (2026-06-01). Per-user trades table, changes
// only on new trade rows landing — 10-min poll is plenty.
const intBgaCoherence = ref<BgaTradeCoherence | null>(null)
// NAV cross-check state (2026-08-06 gold-decision-inputs monitoring).
// Keys change once per NAV poll cycle at most — 5-min poll like the CSV
// freshness probe.
const intNavCrossChecks = ref<NavCrossChecks | null>(null)
// Gold m_t producer's own signal payload (2026-08-07 operator request) —
// read for its `inputs` freshness block (gc_bars/treasury/params_source).
// NOT the fund-scoped intSignal above (gold routes to BGA in the fund
// toggle, so intSignal never carries a GOLD payload). The decision fires
// once at 08:40 London (with same-day retries to ~08:54); a 5-min poll
// like intNavCrossChecks is plenty.
// Plan 2026-10-04 Task 4a: these were per-fund refs owned by Dashboard
// pollers. They now read through FUND_PAGES[slug].feeds via feedData,
// preserving every consumer's shape so useIntegrityChecks's positional
// signature is untouched.
const intGoldSignal = computed<GoldSignalResponse | null>(() => feed<GoldSignalResponse>('goldSignal', feedData))
// Gold per-holding enriched telemetry (2026-08-15 gold-holdings-telemetry
// spec, Task 6). Dedicated ref — the fund-scoped intEnriched above never
// carries a GOLD payload (gold routes to BGA in the fund toggle, same as
// intSignal per the comment above), and its shared "enriched" slot is
// consumed by several BGA/JH-shaped checks (comp-enriched-fields,
// math-weight-sum, etc.) that would misfire on GOLD's minimal payload
// shape. Polled unconditionally (not gated on portfolio === 'gold') so the
// integrity strip stays meaningful from any tab, mirroring intGoldSignal.
// 5s cadence mirrors the BGA/JH intEnriched poll (pollIntegrity, intTimer)
// — HoldingsPanel (Task 5) already polls this same endpoint internally for
// its DIR/RET%/MOM columns; this is a second, independent 5s poller for the
// integrity panel (accepted duplication — see task-6 brief).
const intGoldEnriched = computed<EnrichedSynthNavData | null>(() => {
  const v = feed<EnrichedSynthNavData & { available?: boolean }>('goldEnriched', feedData)
  return v && v.available !== false ? v : null
})
// GGE promotion Phase 4 (2026-08-18): the gge_signal producer's own signal
// payload — read for its `inputs` freshness block (cl_bars/params_source).
// Same dedicated-ref rationale as intGoldSignal (gge routes to BGA in the
// fund toggle, so intSignal never carries a GGE payload). 5-min poll.
const intGgeSignal = computed<GgeSignalResponse | null>(() => feed<GgeSignalResponse>('ggeSignal', feedData))
// K_t per-fund audit (2026-08-23): Gold + GGE are daily-only K_t pipelines
// (no intraday writer — see Program.cs "gold is daily-only" short-circuit)
// AND their Postgres daily rows live in a different write path than
// BGA/JH's synthetic_bga_kt (Gold=0 rows in that table). The C# publisher
// writes `synth_nav:{fund}:kt_latest` to Redis with a fully-formed payload
// (date, kGbp, returnFrac, coverage/coverageTotal, rmseBps, computedAtUtc),
// which is a self-sufficient source for all 4 parity checks: recency,
// residual, coverage, and publisher freshness. Same 5-min cadence as the
// BGA/JH daily-series polls — the publisher fires once daily around 07:35 UK.
const intGoldKtLatest = computed<GoldKtLatestResponse | null>(() => {
  const v = feed<GoldKtLatestResponse & { available?: boolean }>('goldKtLatest', feedData)
  return v && v.available !== false ? v : null
})
const intGgeKtLatest = computed<GgeKtLatestResponse | null>(() => {
  const v = feed<GgeKtLatestResponse & { available?: boolean }>('ggeKtLatest', feedData)
  return v && v.available !== false ? v : null
})
const intExpanded = ref(false)
let intTimer: ReturnType<typeof setInterval> | null = null
let intKtTimer: ReturnType<typeof setInterval> | null = null
let intAvTimer: ReturnType<typeof setInterval> | null = null
let intCsvTimer: ReturnType<typeof setInterval> | null = null
let intDbeqTimer: ReturnType<typeof setInterval> | null = null
let intHoldingsTimer: ReturnType<typeof setInterval> | null = null
let intImrWarmupTimer: ReturnType<typeof setInterval> | null = null
let intBgEffectiveTimer: ReturnType<typeof setInterval> | null = null
let intBgaCoherenceTimer: ReturnType<typeof setInterval> | null = null
let intNavCrossChecksTimer: ReturnType<typeof setInterval> | null = null
let jhNavTimer: ReturnType<typeof setInterval> | null = null

async function pollIntegrity() {
  // 2026-11-02: the signal / enriched polls below are per-user fund routes —
  // wait for /me so the first tick is decided by hasAccess, never fail-open.
  await whenLoaded()
  try {
    if (!hasAccess(fund.value)) intSignal.value = null
    else {
      const data = await api.signal(fund.value)
      intSignal.value = (data as any).available === false ? null : data
    }
  } catch { intSignal.value = null }
  try {
    const pf = pillFund.value
    if (pf === fund.value) pillSignal.value = intSignal.value
    else if (pf === null) pillSignal.value = null
    else {
      const data = await api.signal(pf)
      pillSignal.value = (data as any).available === false ? null : data
    }
  } catch { pillSignal.value = null }
  try {
    // Pass fund explicitly (Plan 2 / sub-C pattern) — don't rely on the
    // server-side FundIdQueryBinder active-fund fallback. Makes the JH path
    // deterministic and matches the HoldingsPanel poller from sub-C T3.
    // 2026-11-02: /synth-nav/enriched is a per-user fund route — skip it for a
    // fund the viewer lacks instead of 403ing every 5 s (BGA fallback on every
    // non-BGA/JH page included).
    if (!hasAccess(fund.value)) intEnriched.value = null
    else {
      const data = await api.enrichedSynthNav(fund.value)
      intEnriched.value = data && (data as any).available !== false ? data : null
    }
  } catch { intEnriched.value = null }
  try {
    const f = fund.value
    intShadowLive.value = (f === 'BGA' || f === 'JH') && hasAccess(f) ? await api.bgaJhSynthNavLive(f) : null
  } catch { intShadowLive.value = null }
  try {
    const data = await api.vixIntraday()
    intVixIntraday.value = data && (data as any).available !== false ? data : null
  } catch { intVixIntraday.value = null }
  try {
    const status = await api.status()
    hlConnected.value = status.loggedIn
  } catch { /* keep previous value */ }
}

// 2026-08-30: keep the previous series on a failed poll (matches the
// status/HL polls above). Nulling it painted three FAIL rows ("No K_t
// series data" — recency / residual / coverage) for up to a 5-min poll
// cycle on any transient failure: a 401 during a session re-auth, an
// api restart, a network blip. A real outage still surfaces — the
// series' calc-date goes stale and the API pill goes red — without
// manufacturing a pipeline alarm from a single failed GET. The very
// first poll failing still leaves the refs null (never-loaded state).
// Plan 2026-10-04 Task 4b: the JH + GGE arms moved into FUND_PAGES.jh/gge.feeds
// (ktJh/ktGge) — this is now BGA-only, renamed for clarity.
async function pollIntegrityKtBga() {
  try {
    intKt.value = await api.bgaKt()
  } catch { /* keep previous value */ }
}

// K_t ticker-attribution (Bga/Jh) + K_t intraday-live (Bga/Jh) pollers moved
// into FUND_PAGES.bga/jh.feeds (ktAttributionBga/ktAttributionJh/
// ktIntradayLive/ktIntradayLiveJh) in Task 4b.
//
// Redis-publisher freshness for the daily K_t scalar (Gold + GGE only;
// BGA/JH publish via the intraday adapter). Reads `synth_nav:{fund}:kt_latest`
// via the api client's thin passthroughs. 5-min cadence — the publisher fires
// once daily around 07:35 UK for gold; the payload's `computedAtUtc` field
// is the freshness signal we key off. Moved into FUND_PAGES.gold/gge.feeds
// (goldKtLatest/ggeKtLatest) in Task 4a.

async function pollIntegrityAvHealth() {
  try {
    const data = await api.alphaVantageHealth()
    intAvHealth.value = data ?? null
  } catch { intAvHealth.value = null }
}

async function pollIntegrityCsvFreshness() {
  try {
    const data = await api.dataCsvFreshness()
    // A payload without a files array (malformed / error body) is "no data", not a
    // render-time TypeError in every integrity check that reads .files.
    intCsvFreshness.value = Array.isArray(data?.files) ? data : null
  } catch { intCsvFreshness.value = null }
}

async function pollIntegrityDatabentoEquity() {
  try {
    const data = await api.databentoEquityHealth()
    intDatabentoEquity.value = data ?? null
  } catch { intDatabentoEquity.value = null }
}

async function pollIntegrityHoldingsStatus() {
  try {
    // 2026-11-02: /holdings/status is a per-user fund route — see pollIntegrity.
    await whenLoaded()
    if (!hasAccess(holdingsStatusFund.value)) { intHoldingsStatus.value = null; return }
    const data = await api.holdingsStatus(holdingsStatusFund.value)
    intHoldingsStatus.value = (data as { available?: boolean }).available === false ? null : data
  } catch { intHoldingsStatus.value = null }
}

async function pollIntegrityImrWarmup() {
  try {
    const data = await api.imrWarmupHealth()
    intImrWarmup.value = data ?? null
  } catch { intImrWarmup.value = null }
}

async function pollIntegrityBgEffective() {
  try {
    const data = await api.bgEffectiveDateHealth()
    intBgEffective.value = data ?? null
  } catch { intBgEffective.value = null }
}

async function pollIntegrityBgaCoherence() {
  try {
    const data = await api.bgaTradeCoherence()
    intBgaCoherence.value = data ?? null
  } catch { intBgaCoherence.value = null }
}

async function pollIntegrityNavCrossChecks() {
  try {
    const data = await api.navCrossChecks()
    intNavCrossChecks.value = data ?? null
  } catch { intNavCrossChecks.value = null }
}

// pollIntegrityGoldSignal/GgeSignal/GoldEnriched moved into FUND_PAGES
// gold/gge.feeds (goldSignal/ggeSignal/goldEnriched) in Task 4a.

onMounted(() => {
  pollIntegrity()
  pollIntegrityKtBga()
  pollIntegrityAvHealth()
  pollIntegrityCsvFreshness()
  pollIntegrityDatabentoEquity()
  pollIntegrityHoldingsStatus()
  pollIntegrityImrWarmup()
  pollIntegrityBgEffective()
  pollIntegrityBgaCoherence()
  pollIntegrityNavCrossChecks()
  intTimer = setInterval(pollIntegrity, 5000)
  intKtTimer = setInterval(pollIntegrityKtBga, 5 * 60 * 1000)
  intAvTimer = setInterval(pollIntegrityAvHealth, 5 * 60 * 1000)
  intCsvTimer = setInterval(pollIntegrityCsvFreshness, 5 * 60 * 1000)
  intDbeqTimer = setInterval(pollIntegrityDatabentoEquity, 60 * 1000)
  intHoldingsTimer = setInterval(pollIntegrityHoldingsStatus, 30 * 1000)
  intImrWarmupTimer = setInterval(pollIntegrityImrWarmup, 60 * 1000)
  intBgEffectiveTimer = setInterval(pollIntegrityBgEffective, 10 * 60 * 1000)
  intBgaCoherenceTimer = setInterval(pollIntegrityBgaCoherence, 10 * 60 * 1000)
  intNavCrossChecksTimer = setInterval(pollIntegrityNavCrossChecks, 5 * 60 * 1000)
})
onUnmounted(() => {
  if (intTimer) clearInterval(intTimer)
  if (intKtTimer) clearInterval(intKtTimer)
  if (intAvTimer) clearInterval(intAvTimer)
  if (intCsvTimer) clearInterval(intCsvTimer)
  if (intDbeqTimer) clearInterval(intDbeqTimer)
  if (intHoldingsTimer) clearInterval(intHoldingsTimer)
  if (intImrWarmupTimer) clearInterval(intImrWarmupTimer)
  if (intBgEffectiveTimer) clearInterval(intBgEffectiveTimer)
  if (intBgaCoherenceTimer) clearInterval(intBgaCoherenceTimer)
  if (intNavCrossChecksTimer) clearInterval(intNavCrossChecksTimer)
  // Follow-up (2026-10-04 housekeeping): pollJhNav's own setInterval (below)
  // was never cleared on unmount — a leaked instance (e.g. the
  // feedTimers-leak scenario exercised in Dashboard.feeds.test.ts) kept
  // calling api.jhNav() every 5 min forever.
  if (jhNavTimer) clearInterval(jhNavTimer)
})

const { results: intResults, summary: intSummary } = useIntegrityChecks(
  intSignal, intEnriched, intVixIntraday, intKt, intAvHealth, intCsvFreshness,
  intDatabentoEquity, intKtIntradayLive, intHoldingsStatus, intImrWarmup,
  intBgEffective, intBgaCoherence, intKtIntradayLiveJh, intNavCrossChecks,
  intGoldSignal, intKtJh, intGoldEnriched, intGgeSignal,
  intGoldKtLatest, intGgeKtLatest, intKtGge,
  intKtAttributionBga, intKtAttributionJh,
  // 2026-11-02: pollIntegrity never fetches signal/enriched for a fund the
  // viewer lacks — skip their checks rather than count 22 "missing" FAILs.
  computed(() => hasAccess(fund.value)),
)

const signalServiceUp = computed(() =>
  signalPillUp(
    pillSignal.value?.timestamp,
    intKtIntradayLive.value?.isUsTradingDay,
  ))

// ── Canonical returns — computed ONCE, passed to all components as plain numbers ──
// Display rule (agreed 2026-04-16):
//   synth = anchor × (1 + overnight_return_gbp)
//   pct   = overnight_return_gbp × 100
// where anchor = today's BG official when published, else prev official.
// This matches the RET% TOTAL column in the holdings panel (which shows
// overnight_return_usd/gbp, the weighted basket return vs yesterday's close).
// Shadow K_t (BGA/JH total_source = shadow): the live value vs the last official NAV, the anchor the hero uses.
const shadowFundReturnPct = computed<number | null>(() => {
  const l = intShadowLive.value
  return l && l.tier !== 'critical' && l.returnVsLastOfficial != null ? l.returnVsLastOfficial * 100 : null
})
const activeFundReturnPct = computed(() => shadowFundReturnPct.value)
const nasReturnPct = computed(() => {
  // Same anchor as BGA's overnight_return_gbp (drift since 10AM rebase). Null pre-rebase.
  const price = intSignal.value?.nas100_price
  const anchor = intEnriched.value?.nq_10am
  if (price == null || anchor == null || anchor <= 0) return null
  return ((price / anchor) - 1) * 100
})
const gbpusdReturn = computed(() => {
  const rate = intSignal.value?.gbpusd_rate
  const ref = intSignal.value?.gbpusd_ref
  if (rate == null || ref == null || ref <= 0) return null
  return ((rate / ref) - 1) * 100
})

// ── JH NAV polling ──
const jhReturnPct = ref<number | null>(null)
const jhSparkValues = ref<number[]>([])
async function pollJhNav() {
  if (portfolio.value !== 'jh') return
  try {
    const n = await api.jhNav()
    jhReturnPct.value = n.returnPct
    jhSparkValues.value = n.sparkline.map(r => r.navPence)
  } catch { /* ignore */ }
}
onMounted(() => { pollJhNav(); jhNavTimer = setInterval(pollJhNav, 300000) })

// Plan 2026-10-04 Task 4a: Japan strip anchor/threshold now read from the
// japanSignal feed (FUND_PAGES.japan.feeds) via feedData instead of the
// Task-3 dedicated pollJapanStrip poller.
const japanSignalFeed = computed<{ anchor_price?: number|null; threshold_bps?: number|null; available?: boolean } | null>(() =>
  feed<{ anchor_price?: number|null; threshold_bps?: number|null; available?: boolean }>('japanSignal', feedData))

// Final-review fix wave (2026-10-04): the deleted pollJapanStrip only wrote
// anchor/threshold when `available !== false`, so an `available:false`
// response (fund closed / no strike yet — distinct from a fetch failure,
// which japanSignal's keepPreviousOnFail already covers) kept whatever was
// last known-good instead of blanking the strip. feedData['japanSignal']
// itself gets overwritten with the new {available:false} object each tick,
// so mirroring that semantic needs its own small ref rather than a pure
// computed over feedData.
const japanLastGood = ref<{ anchorPrice: number | null; thresholdBps: number | null }>(
  { anchorPrice: null, thresholdBps: null })
watch(japanSignalFeed, (v) => {
  if (v && v.available !== false) {
    japanLastGood.value = { anchorPrice: v.anchor_price ?? null, thresholdBps: v.threshold_bps ?? null }
  }
}, { immediate: true })

// Japan fund page UI Task 6: the shared state every FUND_PAGES signalTab
// component receives as its single `ctx` prop.
const signalCtx = computed<SignalTabCtx>(() => ({
  intSignal: intSignal.value,
  intEnriched: intEnriched.value,
  activeFundReturnPct: activeFundReturnPct.value,
  jhReturnPct: jhReturnPct.value,
  jhSparkValues: jhSparkValues.value,
  nasReturnPct: nasReturnPct.value,
  gbpusdReturn: gbpusdReturn.value,
  japanAnchorPrice: japanLastGood.value.anchorPrice,
  japanThresholdBps: japanLastGood.value.thresholdBps,
}))

// Follow-up (2026-10-04 housekeeping): `showNewsTicker` only means anything
// to BgaJhSignalTab (bga/jh share that component; every other fund's
// signalTab doesn't declare the prop). Passing it unconditionally would land
// on FundSignalTab/JapanSignalTab/... as an unresolved attribute —
// harmless visually (they're multi-root templates, so Vue can't fall it
// through to any element) but it trips a dev-mode console.warn that several
// Dashboard tests assert never fires. Gate it on component identity instead.
// Option C (2026-10-11): FundSignalTab (gold, gge) likewise gets its
// registry-declared `config` (FUND_PAGES[slug].signalPanel) and nothing else.
const signalTabExtraProps = computed(() => {
  if (page.value.kind !== 'fund') return {}
  if (page.value.signalTab === BgaJhSignalTab) return { showNewsTicker: page.value.showNewsTicker }
  if (page.value.signalPanel) return { config: page.value.signalPanel }
  return {}
})

const categoryOrder: Category[] = ['freshness', 'completeness', 'math', 'coherence']
const categoryLabels: Record<Category, string> = {
  freshness: 'Freshness', completeness: 'Completeness', math: 'Math', coherence: 'Coherence',
}

// K_t-per-fund audit (2026-08-23): render 4 strategy-scoped K_t sections
// at the TOP of the integrity dropdown, then the existing category-grouped
// remainder with K_t rows filtered OUT (so K_t rows appear exactly once).
// Classification is by check ID (naming is not 1:1 with fund — BGA's
// intraday-family checks are named "K_t intraday …" without the "BGA"
// prefix). See useIntegrityChecks.ts for the ID conventions per fund.
// Japan intentionally absent until Phase 3 (visible via useVisibleFunds when it has data)
type KtFund = 'bga' | 'jh' | 'gold' | 'gge'
const ktFundOrder: KtFund[] = ['bga', 'jh', 'gold', 'gge']
const ktFundLabels: Record<KtFund, string> = {
  bga:  'BGA Kₜ',
  jh:   'JH Kₜ',
  gold: 'Gold Kₜ',
  gge:  'GGE Kₜ',
}
function ktFundOf(id: string): KtFund | null {
  if (/^(?:fresh|cohere|complete)-gold-kt/.test(id)) return 'gold'
  if (/^(?:fresh|cohere|complete)-gge-kt/.test(id))  return 'gge'
  if (/^jh-kt/.test(id) || /^(?:fresh|cohere|complete)-jh-kt/.test(id)) return 'jh'
  // Match `-bga-kt-` pattern (e.g. cohere-bga-kt-top-contributor added
  // 2026-08-23 Phase-1 #3) BEFORE the bare `-kt-` fallback below so it
  // doesn't slip through to the BGA legacy pattern by accident.
  if (/^(?:fresh|cohere|complete)-bga-kt/.test(id))  return 'bga'
  if (/^(?:fresh|cohere|complete)-kt/.test(id))      return 'bga'
  return null
}
const intKtGrouped = computed(() => {
  const g: Record<KtFund, typeof intResults.value> = { bga: [], jh: [], gold: [], gge: [] }
  for (const r of intResults.value) {
    const fund = ktFundOf(r.id)
    if (fund) g[fund].push(r)
  }
  return g
})
const intGrouped = computed(() => {
  const g: Record<Category, typeof intResults.value> = { freshness: [], completeness: [], math: [], coherence: [] }
  for (const r of intResults.value) {
    // Skip K_t rows here — they live in intKtGrouped above.
    if (ktFundOf(r.id) !== null) continue
    g[r.category].push(r)
  }
  return g
})
function sevIcon(sev: string) {
  return sev === 'fail' ? '\u2717' : sev === 'warn' ? '\u26A0' : '\u2713'
}
</script>

<template>
  <div class="dashboard">
    <!-- Bright-yellow manual-sale instruction (T+1 settlement lock, 2026-08-18):
         shown at the very top so it cannot be missed. Self-polls the event feed. -->
    <ManualSellAlertBanner />
    <!-- Red stale-NAV pricing-source alert (spec 2026-09-25): any traded fund Red. -->
    <PricingSourceAlertBanner :link-to-nav="portfolio !== 'otp'" />
    <!-- Red signal-health alert (spec 2026-10-23 §5.4): superuser only, config-dated acknowledgement. -->
    <SignalHealthAlertBanner />
    <!-- Status bar -->
    <div class="status-bar" @click="intExpanded = !intExpanded">
      <!-- Identity pill (spec 2026-09-29, option A): replaces the old
           UserSwitcher header control. Sits at the very start of the
           status bar, before the portfolio pill. Stops its own click
           events from toggling the integrity dropdown (see IdentityPill.vue). -->
      <IdentityPill />
      <BlueBoxPill :portfolio="pageSlug" />
      <span class="sb-pill" :class="apiHealthy ? 'sb-ok' : 'sb-err'">API</span>
      <!-- Signal service pill (Paolo 2026-11-04): a signal-bars icon instead of the word — the phone icon pushed
           the status bar onto a second row at phone width. Same green/red pill; "Signal" stays as the tooltip and
           accessible name. -->
      <span
        v-if="pillFund"
        class="sb-pill sb-icon-pill"
        :class="signalServiceUp ? 'sb-ok' : 'sb-err'"
        role="img"
        aria-label="Signal"
        title="Signal"
        data-testid="signal-pill"
      >
        <!-- Thin antenna (Paolo 2026-11-04): a mast with a dot and two short waves, cropped to its own width so the
             pill is ~6 px narrower than the signal-bars icon was. -->
        <svg class="sb-icon sb-icon-antenna" viewBox="6 2 12 20" aria-hidden="true">
          <path d="M12 10.5V21" />
          <circle cx="12" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
          <path d="M8.7 10.4a4.6 4.6 0 0 1 0-5.8" />
          <path d="M15.3 4.6a4.6 4.6 0 0 1 0 5.8" />
        </svg>
      </span>
      <template v-if="isAuthenticated">
        <span class="sb-pill" :class="hlConnected ? 'sb-ok' : 'sb-off'">HL</span>
        <!-- DevOps agent verdicts (paolo 2026-10-03): P = Platform health,
             S = Strategy analysis. Green HEALTHY / nothing to report, amber
             BENIGN_KNOWN / DEGRADED / unrecognised, red INCIDENT, grey offline. -->
        <span class="sb-pill" :class="agentPlatform.cls" :title="agentPlatform.title" data-testid="agent-pill-platform">P</span>
        <span class="sb-pill" :class="agentStrategy.cls" :title="agentStrategy.title" data-testid="agent-pill-strategy">S</span>
      </template>
      <template v-else>
        <span class="sb-pill sb-off">Auth</span>
      </template>
      <span v-if="intSummary.pass > 0" class="sb-pill sb-int-pass">{{ intSummary.pass }}</span>
      <span v-if="intSummary.warn > 0" class="sb-pill sb-int-warn">{{ intSummary.warn }}</span>
      <span v-if="intSummary.fail > 0" class="sb-pill sb-int-fail">{{ intSummary.fail }}</span>
      <!-- Right end of the bar (Paolo 2026-11-04): the HL phone icon for the user in context (hidden unless the
           phone serves them), then the integrity dropdown toggle. -->
      <span class="sb-right">
        <PhonePill v-if="isAuthenticated" />
        <span class="sb-expand">{{ intExpanded ? '\u25B2' : '\u25BC' }}</span>
      </span>
    </div>
    <!-- Integrity detail dropdown -->
    <div v-if="intExpanded" class="int-detail">
      <!-- DevOps agent verdicts (paolo 2026-10-03) — the detail behind the
           P/S pills: raw status · affected module · run date, plus the
           recommended action when the server sent detail (admin viewers).
           The DevOps button opens the tab for the full history. -->
      <div v-if="isAuthenticated" class="int-cat int-cat-agents" data-testid="agent-rows">
        <div class="int-cat-label">Agents</div>
        <div v-for="r in agentRows" :key="'agent-' + r.agent" class="int-row" :class="'tone-' + r.tone" :data-testid="'agent-row-' + r.agent">
          <span class="int-icon">{{ toneIcon(r.tone) }}</span>
          <span class="int-name">{{ r.label }}</span>
          <span class="int-msg">{{ r.message }}<template v-if="r.action"> — {{ r.action }}</template></span>
          <button type="button" class="int-link" :data-testid="'agent-link-' + r.agent" @click.stop="activeTab = 'devops'">DevOps</button>
        </div>
      </div>
      <!-- K_t per-fund sections (2026-08-23 audit) — render first so
           operators can spot a fund-level pipeline problem at a glance
           without hunting through the category-mixed rows below. -->
      <template v-for="fund in ktFundOrder" :key="'kt-' + fund">
        <div v-if="intKtGrouped[fund].length" class="int-cat int-cat-kt">
          <div class="int-cat-label">{{ ktFundLabels[fund] }}</div>
          <div v-for="r in intKtGrouped[fund]" :key="r.id" class="int-row" :class="'sev-' + r.severity">
            <span class="int-icon">{{ sevIcon(r.severity) }}</span>
            <span class="int-name">{{ r.name }}</span>
            <span class="int-msg">{{ r.message }}</span>
          </div>
        </div>
      </template>
      <!-- Remaining checks, grouped by category (K_t rows filtered out
           above so they appear exactly once). -->
      <template v-for="cat in categoryOrder" :key="cat">
        <div v-if="intGrouped[cat].length" class="int-cat">
          <div class="int-cat-label">{{ categoryLabels[cat] }}</div>
          <div v-for="r in intGrouped[cat]" :key="r.id" class="int-row" :class="'sev-' + r.severity">
            <span class="int-icon">{{ sevIcon(r.severity) }}</span>
            <span class="int-name">{{ r.name }}</span>
            <span class="int-msg">{{ r.message }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Superuser view-only preview of a gated fund (2026-09-28) -->
    <div v-if="showPreviewBadge" class="preview-badge" data-testid="preview-badge" role="status">Preview — not in your dealing plan</div>

    <!-- gold-productionization Task 10 gold-view gate REMOVED here by the
         multi-strategy allocation arc (Task 4, spec §6): with per-user
         fund allocations shipped, the gold subdomain renders for any
         authenticated user like BGA/JH-with-access — dealing stays
         switch-gated via the GOLD allocation row + its arming modal, not
         a view-level banner. See StrategyAllocationCard.vue. A restricted
         JH host now resolves to BGA (usePortfolio), so no banner is needed
         here either (spec 2026-09-30). -->

    <!-- Tab bar -->
    <nav class="tab-bar">
      <button
        v-for="tab in availableTabs"
        :key="tab"
        :class="['tab', { active: activeTab === tab, disabled: tabState(tab).kind === 'disabled' }]"
        :aria-disabled="tabState(tab).kind === 'disabled' ? 'true' : undefined"
        :data-href="homeTabHref(tab) ?? undefined"
        @click="selectTab(tab)"
      >
        {{ tabLabel(tab) }}
      </button>
    </nav>

    <!-- Tab content -->
    <div class="tab-content">
      <!-- Japan fund page UI Task 7: a disabled tab (per FUND_PAGES) shows
           its reason instead of the tab's normal body, on every tab
           including deep links — getTabFromHash already accepts disabled
           tabs (they're in availableTabs), so no separate hash-guard is
           needed here. -->
      <template v-if="tabState(activeTab).kind === 'disabled'">
        <DisabledTabBody :reason="(tabState(activeTab) as any).reason" />
      </template>
      <template v-else>
      <template v-if="activeTab === 'home' && page.kind === 'home'">
        <FofHome />
      </template>

      <template v-if="activeTab === 'signal' && page.kind === 'fund'">
        <component v-if="page.topStrip" :is="page.topStrip" :ctx="signalCtx" />
        <component :is="page.signalTab" :ctx="signalCtx" v-bind="signalTabExtraProps" />
      </template>

      <template v-if="activeTab === 'pnl'">
        <PnlPanel />
      </template>

      <template v-if="activeTab === 'nav'">
        <NavPanel />
      </template>

      <template v-if="activeTab === 'risk'">
        <RiskPanel />
      </template>

      <template v-if="activeTab === 'kt'">
        <KtTrackingPanel />
        <!-- Task 11 (2026-09-16 gold/GGE strike-anchor arc PR D): per-fund
             shadow-anchor freshness (the payload always covers all 4 funds).
             Superuser-only: it surfaces the NEW shadow-mode reconciliation
             pipeline (Tasks 3/7-10), not anything the live dealing/settlement
             path depends on yet — see StrikeAnchorHealthTile.vue's own doc
             comment for the shape rationale vs the OVX single-row precedent.
             2026-09-19 (paolo): moved here from the top of every page — it is
             K_t-pane material, and belongs below the tracking scoreboard. -->
        <StrikeAnchorHealthTile v-if="isSuperUser" />
      </template>

      <template v-if="activeTab === 'account'">
        <AccountPanel />
      </template>

      <template v-if="activeTab === 'exec'">
        <ExecPanel />
      </template>

      <template v-if="activeTab === 'devops'">
        <DevOpsPanel />
      </template>

      <template v-if="activeTab === 'otp'">
        <OtpWindowPanel />
      </template>

      <template v-if="activeTab === 'backtest'">
        <FofBacktestPanel v-if="page.kind === 'home'" />
        <OtpBacktestPanel v-else-if="page.kind === 'overlay'" />
        <component :is="page.backtestTab" v-else-if="page.kind === 'fund' && page.backtestTab"
                   v-bind="page.backtestPanel ? { config: page.backtestPanel } : {}" />
      </template>

      <template v-if="activeTab === 'admin'">
        <AdminPanel />
      </template>

      <template v-if="activeTab === 'strategy'">
        <StrategyPanel />
      </template>
      </template>
    </div>

  </div>
</template>

<style scoped>
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.status-bar {
  display: flex;
  /* Wrap rather than widen the page: the Blue Box pill + page label (2026-11-02) pushed the row 2-8 px
     past a 390 px phone and every mobile smoke's document.scrollWidth check failed. */
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #1a1a1a;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.sb-pill {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
  line-height: 1.4;
}
/* Icon-only pill: same height as a text pill (the icon is sized to the text's line box), narrower padding. */
.sb-icon-pill { display: inline-flex; align-items: center; padding-left: 6px; padding-right: 6px; }
.sb-icon { display: block; width: 1.4em; height: 1.4em; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; }
.sb-icon-antenna { width: 0.84em; stroke-width: 2.2; }
.sb-ok { background: #2d5a2d; color: #4ade80; }
.sb-err { background: #5a2d2d; color: #f87171; }
.sb-off { background: #333; color: #888; }
.sb-warn { background: #5a4a2d; color: #fbbf24; }
.sb-ack { background: #2d3f5a; color: #93c5fd; }
.sb-int-pass { background: #2d5a2d; color: #4ade80; }
.sb-int-warn { background: #5a4a2d; color: #fbbf24; }
.sb-int-fail { background: #5a2d2d; color: #f87171; }

.sb-right { margin-left: auto; display: inline-flex; align-items: center; gap: 8px; }
.sb-expand { color: #666; font-size: 10px; }

/* Superuser view-only preview badge (2026-09-28) — amber, full-width on mobile */
.preview-badge {
  align-self: flex-start;
  max-width: 100%;
  padding: 6px 12px;
  border-radius: 4px;
  background: #5a4a2d;
  border: 1px solid #fbbf24;
  color: #fbbf24;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}
@media (max-width: 600px) {
  .preview-badge { align-self: stretch; text-align: center; }
}

.int-detail {
  background: #161616;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 8px 12px;
  animation: intFadeIn 0.15s ease;
}
@keyframes intFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.int-cat-label { color: #888; font-size: 10px; text-transform: uppercase; margin: 8px 0 3px; letter-spacing: 0.5px; }
.int-cat:first-child .int-cat-label { margin-top: 2px; }
/* K_t per-fund sections (2026-08-23 audit) — subtle left accent so
   operators can tell fund-scoped K_t groups apart from the mixed
   category groups below. */
.int-cat-kt { border-left: 2px solid #3a5a3a; padding-left: 8px; margin-left: -8px; }
.int-cat-kt .int-cat-label { color: #9cc19c; }
/* DevOps agent rows (2026-10-03) — blue-grey accent, tone colours mirror the
   P/S pills so the dropdown reads the same as the bar. */
.int-cat-agents { border-left: 2px solid #3a4a5a; padding-left: 8px; margin-left: -8px; }
.int-cat-agents .int-cat-label { color: #9cb0c1; }
.tone-ok .int-icon { color: #4ade80; }
.tone-warn .int-icon, .tone-warn .int-name, .tone-warn .int-msg { color: #fbbf24; }
.tone-err .int-icon, .tone-err .int-name, .tone-err .int-msg { color: #f87171; }
.tone-off .int-icon, .tone-off .int-msg { color: #888; }
.int-link {
  flex-shrink: 0;
  margin-left: 8px;
  padding: 0 6px;
  border: 1px solid #444;
  border-radius: 3px;
  background: none;
  color: #9cb0c1;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  line-height: 1.6;
  cursor: pointer;
}
.int-link:hover { border-color: #9cb0c1; }
.int-row { display: flex; align-items: center; gap: 6px; padding: 2px 0; font-size: 12px; font-family: 'JetBrains Mono', monospace; }
.int-icon { width: 14px; text-align: center; flex-shrink: 0; }
.int-name { flex-shrink: 0; }
.int-msg { margin-left: auto; color: #666; font-size: 10px; text-align: right; }
.sev-pass .int-icon { color: #4ade80; }
.sev-warn .int-icon { color: #fbbf24; }
.sev-warn .int-name { color: #fbbf24; }
.sev-warn .int-msg { color: #fbbf24; }
.sev-fail .int-icon { color: #f87171; }
.sev-fail .int-name { color: #f87171; }
.sev-fail .int-msg { color: #f87171; }

.tab-bar {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #333;
}

.tab {
  padding: 0.6rem 1.25rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #888;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
}

.tab:hover { color: #ccc; }
.tab.active {
  color: #eee;
  border-bottom-color: #4caf50;
}
/* Japan fund page UI Task 7: greyed but still clickable (no pointer-events:
   none) — clicking a disabled tab must still switch activeTab so its
   DisabledTabBody reason renders. */
.tab.disabled { opacity: 0.45; }
.disabled-tab-body { padding: 16px; color: #aaa; }

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ---- Mobile responsive ---- */
@media (max-width: 768px) {
  .dashboard {
    gap: 0.5rem;
  }

  /* Phone width (Paolo 2026-11-04): one row at 390 px with the phone icon — measured on the live FOF home: the row
     needed ~369 px of a 328 px content box with the "Signal" word; the icon pill, 4 px pill padding, 3 px gaps and
     the smaller identity / Blue Box pills bring it to ~315 px. */
  .status-bar {
    padding: 4px 6px;
    gap: 3px;
  }

  .sb-pill {
    padding: 1px 4px;
    font-size: 10px;
  }
  .sb-icon-pill { padding-left: 4px; padding-right: 4px; }
  .sb-right { gap: 4px; }

  .int-row { font-size: 11px; }
  .int-msg { font-size: 9px; }

  .tab-bar {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .tab-bar::-webkit-scrollbar {
    display: none;
  }

  .tab {
    padding: 0.75rem 1rem;
    min-height: 44px;
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  .tab-content {
    gap: 0.75rem;
  }
}
</style>
